import { useCallback, useState } from 'react';

export interface GpsPosition {
	latitudine: number;
	longitudine: number;
	accuratezza_gps_m: number;
	gps_position_timestamp: string;
}

export type GpsStatus = 'idle' | 'loading' | 'success' | 'error';

const GPS_TIMEOUT = 10_000; // 10 seconds

export function useGeolocation() {
	const [status, setStatus] = useState<GpsStatus>('idle');
	const [error, setError] = useState<string | null>(null);

	const getPosition = useCallback((): Promise<GpsPosition> => {
		return new Promise((resolve, reject) => {
			if (!navigator.geolocation) {
				const msg = 'GPS non supportato dal dispositivo';
				setStatus('error');
				setError(msg);
				reject(new Error(msg));
				return;
			}

			setStatus('loading');
			setError(null);

			navigator.geolocation.getCurrentPosition(
				(pos) => {
					const result: GpsPosition = {
						latitudine: pos.coords.latitude,
						longitudine: pos.coords.longitude,
						accuratezza_gps_m: pos.coords.accuracy,
						gps_position_timestamp: new Date(pos.timestamp).toISOString(),
					};
					setStatus('success');
					resolve(result);
				},
				(err) => {
					let msg: string;
					switch (err.code) {
						case err.PERMISSION_DENIED:
							msg = 'Permesso GPS negato. Attiva la geolocalizzazione nelle impostazioni.';
							break;
						case err.POSITION_UNAVAILABLE:
							msg = 'Posizione non disponibile';
							break;
						case err.TIMEOUT:
							msg = 'Timeout GPS. Riprova all\'aperto.';
							break;
						default:
							msg = 'Errore GPS sconosciuto';
					}
					setStatus('error');
					setError(msg);
					reject(new Error(msg));
				},
				{
					enableHighAccuracy: true,
					timeout: GPS_TIMEOUT,
					maximumAge: 30_000, // Accept cached position up to 30s old
				},
			);
		});
	}, []);

	/** Try to get GPS position; returns null if unavailable/denied/timeout (never throws) */
	const tryGetPosition = useCallback(async (): Promise<GpsPosition | null> => {
		try {
			return await getPosition();
		} catch {
			return null;
		}
	}, [getPosition]);

	return { getPosition, tryGetPosition, status, error };
}
